import { useMutation, useQuery } from '@tanstack/react-query';

import pillarService from './pillarService';

import type {
    CreatePillarRequest,
    Pillar,
    PillarObjectiveAlignment,
    UpdatePillarRequest,
} from '@/types/pillars';

import { invalidatePillarSummaries } from '@/api/objectives';
import { queryClient } from '@/AppWithProviders';
import { ObjectiveQueryKey, PillarMutationKey, PillarQueryKey } from '@/constants/QueryKeys';

type UpdatePillarParams = { id: string; request: UpdatePillarRequest };

/**
 * A pillar's name, icon, colour and status are echoed by the objective list and by every pillar
 * summary, so every pillar mutation has to refresh those alongside the pillar list.
 */
const invalidatePillars = () => {
    queryClient.invalidateQueries({ queryKey: [PillarQueryKey.PILLARS] });
    queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.OBJECTIVES] });
    invalidatePillarSummaries();
    queryClient.invalidateQueries({ queryKey: [PillarQueryKey.OBJECTIVE_ALIGNMENT] });
};

export const useGetPillars = () => {
    return useQuery<Pillar[]>({
        queryKey: [PillarQueryKey.PILLARS],
        queryFn: () => pillarService.getPillars(),
    });
};

export const useGetPillarObjectiveAlignment = () => {
    return useQuery<PillarObjectiveAlignment>({
        queryKey: [PillarQueryKey.OBJECTIVE_ALIGNMENT],
        queryFn: () => pillarService.getObjectiveAlignment(),
    });
};

export const useGetPillarById = (id: string) => {
    return useQuery<Pillar>({
        queryKey: [PillarQueryKey.PILLAR, id],
        queryFn: () => pillarService.getPillarById(id),
        enabled: !!id,
    });
};

export const useCreatePillar = () => {
    return useMutation<Pillar, Error, CreatePillarRequest>({
        mutationKey: [PillarMutationKey.CREATE_PILLAR],
        mutationFn: (request) => pillarService.createPillar(request),
        onSuccess: invalidatePillars,
    });
};

export const useUpdatePillar = () => {
    return useMutation<Pillar, Error, UpdatePillarParams>({
        mutationKey: [PillarMutationKey.UPDATE_PILLAR],
        mutationFn: ({ id, request }) => pillarService.updatePillar(id, request),
        onSuccess: () => {
            invalidatePillars();
            queryClient.invalidateQueries({ queryKey: [PillarQueryKey.PILLAR] });
        },
    });
};

export const useDeletePillar = () => {
    return useMutation<Pillar, Error, string>({
        mutationKey: [PillarMutationKey.DELETE_PILLAR],
        mutationFn: (id) => pillarService.deletePillar(id),
        onSuccess: invalidatePillars,
    });
};

export const useTogglePillarStatus = () => {
    return useMutation<Pillar, Error, string>({
        mutationKey: [PillarMutationKey.TOGGLE_STATUS],
        mutationFn: (id) => pillarService.togglePillarStatus(id),
        onSuccess: invalidatePillars,
    });
};
